import { useSubjectLanguageVariants } from '@features/user';
import {
  FiberManualRecord as StatusIcon,
  Wifi as OnlineIcon,
  WifiOff as OfflineIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';

import type { WaitingRoomProviderAvailability } from '../types';

interface WaitingRoomAvailabilityControlProps {
  availability: WaitingRoomProviderAvailability;
  isLoading?: boolean;
  disabled?: boolean;
  showToggle?: boolean;
  /**
   * The provider keeps their waiting room always open (GDN-2947): check-in no
   * longer depends on live presence, so the manual online/offline toggle is
   * hidden and the caption reflects that check-ins never pause. The
   * online/offline status label still shows real heartbeat presence.
   */
  alwaysOpen?: boolean;
  variant?: 'light' | 'dark';
  onChange?: (availability: WaitingRoomProviderAvailability) => void;
}

interface WaitingRoomAvailabilityControlPresentationProps extends WaitingRoomAvailabilityControlProps {
  subjectLanguagePluralCap: string;
}

export function WaitingRoomAvailabilityControlPresentation({
  availability,
  subjectLanguagePluralCap,
  isLoading = false,
  disabled = false,
  showToggle = true,
  alwaysOpen = false,
  variant = 'light',
  onChange,
}: WaitingRoomAvailabilityControlPresentationProps) {
  const isOnline = availability === 'online';
  const isDark = variant === 'dark';
  const shouldShowToggle = showToggle && !alwaysOpen;
  const caption = alwaysOpen
    ? `Always open — ${subjectLanguagePluralCap.toLowerCase()} can check in anytime`
    : isOnline
      ? `${subjectLanguagePluralCap} can check in`
      : 'Check-ins are paused';
  const statusColor = isOnline ? 'success.main' : 'text.disabled';
  const switchSx = isDark
    ? {
        '& .MuiSwitch-track': {
          bgcolor: 'rgba(255,255,255,0.28)',
          opacity: 1,
        },
        '& .MuiSwitch-switchBase': {
          color: 'grey.500',
          '&.Mui-checked': {
            color: 'primary.light',
            '& + .MuiSwitch-track': {
              bgcolor: 'primary.main',
              opacity: 1,
            },
          },
        },
        '& .MuiSwitch-thumb': {
          boxShadow: '0 0 0 1px rgba(255,255,255,0.18)',
        },
      }
    : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: isDark ? 'grey.800' : 'action.hover',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
        {isLoading ? (
          <CircularProgress size={16} color={isDark ? 'inherit' : 'primary'} />
        ) : (
          <StatusIcon sx={{ fontSize: 14, color: statusColor }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: isDark ? 'common.white' : 'text.primary' }}
          >
            {isOnline ? 'Online' : 'Offline'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isDark ? 'grey.400' : 'text.secondary', display: 'block' }}
          >
            {caption}
          </Typography>
        </Box>
      </Stack>

      {shouldShowToggle ? (
        <FormControlLabel
          control={
            <Switch
              checked={isOnline}
              disabled={disabled || isLoading || !onChange}
              onChange={(_, checked) => onChange?.(checked ? 'online' : 'offline')}
              sx={switchSx}
              slotProps={{
                input: {
                  'aria-label': isOnline ? 'Go offline' : 'Go online',
                },
              }}
            />
          }
          label={isOnline ? <OnlineIcon fontSize="small" /> : <OfflineIcon fontSize="small" />}
          labelPlacement="start"
          sx={{
            m: 0,
            color: isDark ? 'grey.300' : 'text.secondary',
            '.MuiFormControlLabel-label': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      ) : (
        <Box sx={{ color: isDark ? 'grey.300' : 'text.secondary', display: 'flex' }}>
          {isOnline ? <OnlineIcon fontSize="small" /> : <OfflineIcon fontSize="small" />}
        </Box>
      )}
    </Box>
  );
}

export function WaitingRoomAvailabilityControl(props: WaitingRoomAvailabilityControlProps) {
  const { pluralCap } = useSubjectLanguageVariants();

  return (
    <WaitingRoomAvailabilityControlPresentation {...props} subjectLanguagePluralCap={pluralCap} />
  );
}
